/**
 * Mode switching, bone selection, project meta and the small UI channels (toast / busy).
 *
 * Mode guards implement SPEC "Editor Modes": the user must never be able to author animation on
 * a rig that is not ready, and rest-pose editing must not be reachable while animating.
 */

import type { CanonicalBoneId } from '../../shared';
import type {
  EditorMode,
  EditorState,
  LastExportSummary,
  ProjectMeta,
  RigState,
} from '../types';
import { showToast, type ActionContext } from './context';

/** True when the project can be posed: a valid mapping (rigged) or a generated skin (unrigged). */
export function isRigReadyForAnimate(state: EditorState): boolean {
  const project = state.project;
  if (!project || !project.loaded) return false;
  if (project.kind === 'rigged') return state.mapping.validation?.ok === true;
  return state.rig.placement !== null && state.rig.skinGenerated;
}

/** Guard behind `setMode`. UI modules use it to disable mode buttons instead of guessing. */
export function canEnterMode(state: EditorState, mode: EditorMode): boolean {
  switch (mode) {
    case 'model':
    case 'library':
      return true;
    case 'map':
      return state.project !== null && state.project.kind === 'rigged';
    case 'rig':
      return state.project !== null && state.project.kind === 'unrigged';
    case 'animate':
      return isRigReadyForAnimate(state);
    case 'export':
      /*
       * A character exports as a rig-only GLB, so clips are not required — but a bound rig is:
       * there is nothing to write without one. `rigBound` mirrors `session.rig`, which is what
       * the export controller actually reaches for.
       */
      return state.project !== null && state.project.loaded && state.rigBound;
    default:
      return false;
  }
}

/** Why `setMode` refused, for the toast. */
export function modeRejectionReason(state: EditorState, mode: EditorMode): string {
  switch (mode) {
    case 'map':
      return 'MAP mode is only available for an imported rigged model.';
    case 'rig':
      return 'RIG mode is only available for an unrigged model.';
    case 'animate':
      if (!state.project || !state.project.loaded) return 'Load a model before animating.';
      return state.project.kind === 'rigged'
        ? 'Finish and validate the bone mapping before animating.'
        : 'Place the skeleton and generate skin weights before animating.';
    case 'export':
      if (!state.project || !state.project.loaded) return 'Load a model before exporting.';
      return 'Export needs a bound rig — confirm the mapping (MAP) or generate skin weights (RIG).';
    default:
      return `Cannot switch to ${mode} mode.`;
  }
}

export interface ModeActions {
  setMode(mode: EditorMode): void;
  selectBone(bone: CanonicalBoneId | null): void;
  hoverBone(bone: CanonicalBoneId | null): void;
  setProject(project: ProjectMeta | null): void;
  /** Mirror of `session.rig !== null`; dispatched by the controllers beside every bind/clear. */
  setRigBound(bound: boolean): void;
  setLastExport(summary: LastExportSummary | null): void;
  toast(kind: 'info' | 'error', message: string): void;
  setBusy(label: string | null): void;
}

export function createModeActions(ctx: ActionContext): ModeActions {
  return {
    setMode(mode: EditorMode): void {
      const state = ctx.getState();
      if (state.mode === mode) return;
      if (!canEnterMode(state, mode)) {
        showToast(ctx, 'error', modeRejectionReason(state, mode));
        return;
      }

      // Leaving (or entering) RIG drops the transient skin UI: the test-pose preview is a RIG-mode
      // display and an armed region tool must never fire a mesh click made in another mode.
      const rig: RigState = { ...state.rig, previewPose: null, skinTool: 'none' };
      if (mode === 'animate') {
        // Entering ANIMATE ends rest editing (SPEC: no accidental rest-pose modification).
        rig.selectedJoint = null;
      }
      const patch: Partial<EditorState> = { mode, rig };
      if (state.mode === 'animate' && state.playhead.playing) {
        patch.playhead = { ...state.playhead, playing: false };
      }
      ctx.set(patch);
    },

    selectBone(bone: CanonicalBoneId | null): void {
      const state = ctx.getState();
      if (state.selection.bone === bone) return;
      ctx.set({ selection: { bone } });
    },

    hoverBone(bone: CanonicalBoneId | null): void {
      if (ctx.getState().hover === bone) return;
      ctx.set({ hover: bone });
    },

    setProject(project: ProjectMeta | null): void {
      const state = ctx.getState();
      const patch: Partial<EditorState> = { project };
      if (!project) {
        patch.mode = 'model';
        patch.selection = { bone: null };
        patch.hover = null;
      } else if (!canEnterMode({ ...state, project }, state.mode)) {
        patch.mode = 'model';
      }
      ctx.set(patch);
    },

    setRigBound(bound: boolean): void {
      const state = ctx.getState();
      if (state.rigBound === bound) return;
      const patch: Partial<EditorState> = { rigBound: bound };
      // Losing the rig invalidates EXPORT exactly the way closing the project does, so it falls
      // back the same way rather than leaving the user in a mode whose gate no longer holds.
      if (!canEnterMode({ ...state, rigBound: bound }, state.mode)) patch.mode = 'model';
      ctx.set(patch);
    },

    setLastExport(summary: LastExportSummary | null): void {
      const ui = ctx.getState().ui;
      if (ui.lastExport === summary) return;
      ctx.set({ ui: { ...ui, lastExport: summary } });
    },

    toast(kind: 'info' | 'error', message: string): void {
      showToast(ctx, kind, message);
    },

    setBusy(label: string | null): void {
      const ui = ctx.getState().ui;
      if (ui.busy === label) return;
      ctx.set({ ui: { ...ui, busy: label } });
    },
  };
}
